/**
 * Enterprise login challenge handling (security contract EL-1 / EL-2).
 *
 * GET /v1/enterprise/auth/challenge returns the message the user's wallet + key
 * co-sign and, from EL-2 on, a `challengeToken`: 32 random bytes (64 hex) that
 * bind the challenge to the tab that requested it. The server stores only its
 * sha256 and refuses to consume the challenge unless the POST that redeems it
 * (login or link-WK) carries the token. Someone who merely observes the signed
 * challenge on the relay therefore cannot race the real user to a session.
 *
 * The token is a bearer secret for the few seconds of the sign-in: it lives in
 * memory only (a local variable between the GET and the POST). Never put it in
 * Redux, localStorage, sessionStorage, the URL or a log line.
 *
 * The message itself is passed to the wallet exactly as the server sent it. We
 * only check that it is one of the two login grammars, so a tampered challenge
 * response can never route a critical-action, xpub or recovery message through
 * the sign-in prompt.
 */

/** A login challenge as served by the relay. */
export interface LoginChallenge {
  /** The exact text the wallet and key sign. Never rewritten client-side. */
  message: string;
  /** EL-2 requester binding. Absent only on pre-EL-2 servers. Memory only. */
  challengeToken?: string;
  expiresAt?: string;
}

/** Wire shape of the challenge endpoint's `data`. */
export interface LoginChallengeResponseData {
  message?: unknown;
  challengeToken?: unknown;
  expiresAt?: unknown;
}

const CHALLENGE_TOKEN_RE = /^[0-9a-f]{64}$/;
/** v1 (pre EL-1): 13-digit ms timestamp immediately followed by 32 hex. */
const LOGIN_V1_RE = /^\d{13}[0-9a-f]{32}$/;
/** v2 (EL-1): `${ts13}:SSP_ENTERPRISE_LOGIN:${nonce32}:${origin}` — origin last (it has colons). */
const LOGIN_V2_RE = /^(\d{13}):SSP_ENTERPRISE_LOGIN:([0-9a-f]{32}):(https?:\/\/[^\s]+)$/;
const MAX_MESSAGE_LENGTH = 500;

export type LoginChallengeErrorCode =
  | 'INVALID_RESPONSE'
  | 'INVALID_MESSAGE'
  | 'INVALID_TOKEN'
  | 'ORIGIN_MISMATCH';

export class LoginChallengeError extends Error {
  readonly code: LoginChallengeErrorCode;
  constructor(code: LoginChallengeErrorCode, message: string) {
    super(message);
    this.name = 'LoginChallengeError';
    this.code = code;
  }
}

/** Origin embedded in a v2 login challenge, or null for v1 / anything else. */
export function loginChallengeOrigin(message: string): string | null {
  const match = LOGIN_V2_RE.exec(message);
  return match?.[3] ?? null;
}

/**
 * Validate the challenge endpoint's payload. Fails closed: anything that is not
 * a well-formed login challenge (with a well-formed token when one is present)
 * throws, so the caller never asks the wallet to sign it.
 *
 * @param appOrigin the origin this app runs on. A v2 challenge issued for a
 *   different origin is refused here — the wallet would refuse it too, and the
 *   user gets a clear error instead of a confusing device prompt.
 */
export function parseLoginChallenge(data: unknown, appOrigin: string): LoginChallenge {
  if (!data || typeof data !== 'object') {
    throw new LoginChallengeError('INVALID_RESPONSE', 'Invalid challenge response');
  }
  const d = data as LoginChallengeResponseData;

  const message = d.message;
  if (
    typeof message !== 'string' ||
    message.length > MAX_MESSAGE_LENGTH ||
    !(LOGIN_V1_RE.test(message) || LOGIN_V2_RE.test(message))
  ) {
    throw new LoginChallengeError('INVALID_MESSAGE', 'The server sent an invalid login challenge');
  }

  const origin = loginChallengeOrigin(message);
  if (origin !== null && origin !== appOrigin) {
    throw new LoginChallengeError(
      'ORIGIN_MISMATCH',
      'The login challenge was issued for a different site'
    );
  }

  let challengeToken: string | undefined;
  if (d.challengeToken !== undefined && d.challengeToken !== null) {
    if (typeof d.challengeToken !== 'string' || !CHALLENGE_TOKEN_RE.test(d.challengeToken)) {
      throw new LoginChallengeError('INVALID_TOKEN', 'The server sent an invalid challenge token');
    }
    challengeToken = d.challengeToken;
  }

  return {
    message,
    ...(challengeToken ? { challengeToken } : {}),
    ...(typeof d.expiresAt === 'string' ? { expiresAt: d.expiresAt } : {}),
  };
}
